import { useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useToast } from '@/components/ui/Toast';
import { landingRouteForRole, ROUTES } from '@/app/router/paths';
import { useGoogleLoginMutation, type GoogleSignupRequired } from './api';
import { authErrorCode, parseAuthError } from './errors';

/** O que a tela de cadastro precisa saber de quem chegou pelo Google. */
export interface GoogleSignupHint {
  idToken: string;
  email: string;
  name: string;
}

/** Estado de rota carregado até /cadastro quando o e-mail do Google não tem conta. */
export interface GoogleRouteState {
  google?: GoogleSignupHint;
}

/**
 * O que acontece quando alguém conclui o consentimento do Google.
 *
 * 🔑 Um caminho só, nas duas telas. "Entrar com Google" e "Cadastrar com Google" são a
 * mesma ação — só o servidor sabe se aquele e-mail já tem conta, e é ele quem decide:
 * sessão, ou desvio para a tela de empresa. Duas implementações divergiriam, e a
 * divergência apareceria como "pelo cadastro dá, pelo login não".
 */
export function useGoogleAuth() {
  const navigate = useNavigate();
  const location = useLocation();
  const toast = useToast();
  const [googleLogin, { isLoading }] = useGoogleLoginMutation();

  const onCredential = useCallback(
    async (idToken: string) => {
      try {
        const session = await googleLogin({ idToken }).unwrap();
        const target =
          (location.state as { from?: { pathname?: string } } | null)?.from?.pathname ??
          landingRouteForRole(session.user.role);
        navigate(target, { replace: true });
      } catch (err) {
        const code = authErrorCode(err as never);
        if (code === 'GOOGLE_SIGNUP_REQUIRED') {
          // Não é erro: é o e-mail ainda não ter conta. Leva os dados que o Google já
          // deu — nome e e-mail — e o token, que volta ao back para ser reverificado.
          const body = ((err as { data?: unknown }).data ?? {}) as GoogleSignupRequired;
          const state: GoogleRouteState = {
            google: { idToken, email: body.email, name: body.name },
          };
          navigate(ROUTES.signup, { state });
          return;
        }
        toast.danger({
          title: 'Não foi possível entrar com o Google',
          description: parseAuthError(err as never),
        });
      }
    },
    [googleLogin, location.state, navigate, toast],
  );

  return { onCredential, isLoading };
}
